import { Launch } from '@/redux/slices/focusNewScheduleSlice';
import { ScheduleAvailType, TitleEntity } from '@/redux/slices/slice_types';
import { territoryGrouping, TerritoryGrouping } from '../constants';
import { IncrementalLaunchAvailChipItem } from '../AvailsSchedule/NewSchedule/ReviewSchedule/IncrementalLaunchAvailChip';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';

export type GroupRights = {
  label: string;
  territories: TerritoryCode[];
};

export type TerritoryCode = TerritoryGrouping[keyof TerritoryGrouping][number]['code'];

export type ResolvedLaunchRightsState = {
  selectedTerritories: string[];
  selectedDubs: string[];
  selectedSubs: string[];
  selectedVersions: (TitleEntity | TitleEntityV2)['id'][];
};

export type LaunchValidationExpansionState = {
  addedTerritories: string[];
  addedDubs: string[];
  addedSubs: string[];
  addedVersions: (TitleEntity | TitleEntityV2)['id'][];
  resolvedTerritories: string[];
  resolvedDubs: string[];
  resolvedSubs: string[];
  resolvedVersions: (TitleEntity | TitleEntityV2)['id'][];
};

export const groupTerritoriesByRegion = (territories: TerritoryCode[], grouping: TerritoryGrouping): GroupRights[] => {
  if (!territories.length) return [];

  return Object.entries(grouping)
    .map(([groupLabel, regionItems]) => {
      const regionCodes = regionItems.map((regionItem) => regionItem.code);

      const matchingTerritories = territories
        .map((t) => t.toUpperCase() as TerritoryCode)
        .filter((territoryCode) => regionCodes.includes(territoryCode));

      return {
        label: groupLabel,
        territories: matchingTerritories
      };
    })
    .filter((group) => group.territories.length > 0);
};

export const getAvailSourceByType = (
  type: ScheduleAvailType | 'ungroupTer',
  audioOptions?: { code: string }[],
  timedTextOptions?: { code: string }[]
): {
  groupRights?: GroupRights[];
  rightsList?: string[];
} => {
  if (type === 'territories') {
    return {
      groupRights: Object.entries(territoryGrouping).map(([groupLabel, territories]) => ({
        label: groupLabel,
        territories: territories.map((territory) => territory.code)
      })),
      rightsList: undefined
    };
  }

  if (type === 'ungroupTer') {
    const flatTerritories = Object.values(territoryGrouping).flatMap((group) => group.map((territory) => territory.code));
    return {
      groupRights: undefined,
      rightsList: flatTerritories
    };
  }

  if (type === 'audio') {
    return {
      groupRights: undefined,
      rightsList: audioOptions?.map((audio) => audio.code.replace('_', '-'))
    };
  }

  return {
    groupRights: undefined,
    rightsList: timedTextOptions?.map((subtitle) => subtitle.code.replace('_', '-'))
  };
};

export const formatAvailDisplayLabel = (
  code: string,
  type: ScheduleAvailType | 'versions',
  regionNamesInEnglish: Intl.DisplayNames,
  allLanguagesMap?: Record<string, { label: string }>,
  allSelectedVersions?: (TitleEntity | TitleEntityV2)[]
): string => {
  if (type === 'versions') {
    return allSelectedVersions?.find((sv) => sv.id === Number(code))?.alpha_id ?? code;
  } else if (type === 'territories') {
    const upperCode = code.toUpperCase();
    const regionName = regionNamesInEnglish.of(upperCode);
    return `${regionName ?? code} [${upperCode}]`;
  }

  const languageLabel = allLanguagesMap?.[code.replace('-', '_')]?.label;
  return `${languageLabel ?? code} [${code}]`;
};

export const createAvailChipItems = ({
  values,
  type,
  allLanguagesMap,
  allSelectedVersions,
  isRemoved = false
}: {
  values: string[];
  type: ScheduleAvailType | 'versions';
  allLanguagesMap?: Record<string, { label: string }>;
  allSelectedVersions?: (TitleEntity | TitleEntityV2)[];
  isRemoved?: boolean;
}): IncrementalLaunchAvailChipItem[] => {
  const regionNamesInEnglish = new Intl.DisplayNames(['en'], { type: 'region' });

  return values
    ?.map((value) => ({
      value,
      label: formatAvailDisplayLabel(value, type, regionNamesInEnglish, allLanguagesMap, allSelectedVersions),
      ...(isRemoved ? { isRemoved: true } : {})
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const createSortedAvailChipItems = ({
  addedValues,
  removedValues,
  type,
  allLanguagesMap,
  allSelectedVersions
}: {
  addedValues: string[];
  removedValues: string[];
  type: ScheduleAvailType | 'versions';
  allLanguagesMap?: Record<string, { label: string }>;
  allSelectedVersions?: (TitleEntity | TitleEntityV2)[];
}): IncrementalLaunchAvailChipItem[] => {
  const added = createAvailChipItems({
    values: addedValues,
    type,
    allLanguagesMap,
    allSelectedVersions
  });

  const removed = createAvailChipItems({
    values: removedValues,
    type,
    allLanguagesMap,
    allSelectedVersions,
    isRemoved: true
  });

  return [...added, ...removed].sort((a, b) => a.label.localeCompare(b.label));
};

export const getOccurrenceIso = (baseStartDate: string, frequency: string, index: number) => {
  const d = new Date(baseStartDate);

  if (frequency === 'weekly') d.setDate(d.getDate() + index * 7);
  if (frequency === 'monthly') d.setMonth(d.getMonth() + index);

  return d.toISOString();
};

export const formatDateTimePartsLocal = (
  isoString: string,
  locale = 'en-US',
  dateFormat: 'verbose' | 'short' = 'verbose'
): {
  date: string;
  time: string;
} => {
  const d = new Date(isoString);

  const date = d.toLocaleDateString(locale, {
    ...(dateFormat === 'verbose'
      ? {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }
      : {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        })
  });

  const time = d.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  const tzParts = new Intl.DateTimeFormat(locale, {
    timeZoneName: 'short',
    hour: 'numeric'
  }).formatToParts(d);

  const tzLabel = tzParts.find((p) => p.type === 'timeZoneName')?.value ?? '';

  return {
    date,
    time: tzLabel ? `${time} ${tzLabel}` : time
  };
};

export const joinOrDash = (items: string[]) => (items.length ? items.join(', ') : '—');

/**
 * Applies one launch rights draft onto a previously resolved list while keeping values unique.
 */
export const applyLaunchRightsDraft = ({
  previousValues,
  addedValues = [],
  removedValues = []
}: {
  previousValues: string[];
  addedValues?: string[];
  removedValues?: string[];
}): string[] => {
  const removedSet = new Set(removedValues);
  const nextValues = previousValues.filter((value) => !removedSet.has(value));

  addedValues.forEach((value) => {
    if (!nextValues.includes(value)) {
      nextValues.push(value);
    }
  });

  return nextValues;
};

/**
 * Applies one launch versions draft onto a previously resolved list while keeping values unique.
 */
export const applyLaunchVersionsDraft = ({
  previousValues,
  addedValues = [],
  removedValues = []
}: {
  previousValues: (TitleEntity | TitleEntityV2)['id'][];
  addedValues?: (TitleEntity | TitleEntityV2)['id'][];
  removedValues?: (TitleEntity | TitleEntityV2)['id'][];
}): (TitleEntity | TitleEntityV2)['id'][] => {
  const removedSet = new Set(removedValues);
  const nextValues = previousValues.filter((value) => !removedSet.has(value));

  addedValues.forEach((value) => {
    if (!nextValues.includes(value)) {
      nextValues.push(value);
    }
  });

  return nextValues;
};

/**
 * Returns only incremental launches that include the provided version.
 */
export const getLaunchesForVersion = ({ launchList, versionId }: { launchList: Launch[]; versionId: number }): Launch[] =>
  (launchList ?? []).filter((launch) => launch?.type === 'incremental' && !launch?.incrementalLaunchVersions.remove.find((v) => v === versionId));

/**
 * Resolves cumulative rights state before the specified launch for a given version.
 */
export const getResolvedRightsStateBeforeLaunch = ({
  launchList,
  versionId,
  launchId,
  originalSelectedTerritories,
  originalSelectedDubs,
  originalSelectedSubs,
  originalSelectedVersions
}: {
  launchList: Launch[];
  versionId: number;
  launchId: string;
  originalSelectedTerritories: string[];
  originalSelectedDubs: string[];
  originalSelectedSubs: string[];
  originalSelectedVersions: (TitleEntity | TitleEntityV2)['id'][];
}): ResolvedLaunchRightsState => {
  let selectedTerritories = [...originalSelectedTerritories];
  let selectedDubs = [...originalSelectedDubs];
  let selectedSubs = [...originalSelectedSubs];
  let selectedVersions = [...originalSelectedVersions];

  const relevantLaunches = getLaunchesForVersion({ launchList, versionId });

  for (const launch of relevantLaunches) {
    if (launch.id === launchId) {
      break;
    }

    const territoryDraft = launch.incrementalLaunchRightsDraftByTab?.territories;
    const audioDraft = launch.incrementalLaunchRightsDraftByTab?.audio;
    const timedtextDraft = launch.incrementalLaunchRightsDraftByTab?.timedtext;
    const versionDraft = launch.incrementalLaunchVersions;

    selectedTerritories = applyLaunchRightsDraft({
      previousValues: selectedTerritories,
      addedValues: territoryDraft?.add,
      removedValues: territoryDraft?.remove
    });

    selectedDubs = applyLaunchRightsDraft({
      previousValues: selectedDubs,
      addedValues: audioDraft?.add,
      removedValues: audioDraft?.remove
    });

    selectedSubs = applyLaunchRightsDraft({
      previousValues: selectedSubs,
      addedValues: timedtextDraft?.add,
      removedValues: timedtextDraft?.remove
    });

    selectedVersions = applyLaunchVersionsDraft({
      previousValues: selectedVersions,
      addedValues: versionDraft?.add,
      removedValues: versionDraft?.remove
    });
  }

  return {
    selectedTerritories,
    selectedDubs,
    selectedSubs,
    selectedVersions
  };
};

/**
 * Resolves cumulative rights state through and including the specified launch for a given version.
 */
export const getResolvedRightsStateAtLaunch = ({
  launchList,
  versionId,
  launchId,
  originalSelectedTerritories,
  originalSelectedDubs,
  originalSelectedSubs,
  originalSelectedVersions
}: {
  launchList: Launch[];
  versionId: number;
  launchId: string;
  originalSelectedTerritories: string[];
  originalSelectedDubs: string[];
  originalSelectedSubs: string[];
  originalSelectedVersions: (TitleEntity | TitleEntityV2)['id'][];
}): ResolvedLaunchRightsState => {
  let selectedTerritories = [...originalSelectedTerritories];
  let selectedDubs = [...originalSelectedDubs];
  let selectedSubs = [...originalSelectedSubs];
  let selectedVersions = [...originalSelectedVersions];

  const relevantLaunches = getLaunchesForVersion({ launchList, versionId });

  for (const launch of relevantLaunches) {
    const territoryDraft = launch.incrementalLaunchRightsDraftByTab?.territories;
    const audioDraft = launch.incrementalLaunchRightsDraftByTab?.audio;
    const timedtextDraft = launch.incrementalLaunchRightsDraftByTab?.timedtext;
    const versionDraft = launch.incrementalLaunchVersions;

    selectedTerritories = applyLaunchRightsDraft({
      previousValues: selectedTerritories,
      addedValues: territoryDraft?.add,
      removedValues: territoryDraft?.remove
    });

    selectedDubs = applyLaunchRightsDraft({
      previousValues: selectedDubs,
      addedValues: audioDraft?.add,
      removedValues: audioDraft?.remove
    });

    selectedSubs = applyLaunchRightsDraft({
      previousValues: selectedSubs,
      addedValues: timedtextDraft?.add,
      removedValues: timedtextDraft?.remove
    });

    selectedVersions = applyLaunchVersionsDraft({
      previousValues: selectedVersions,
      addedValues: versionDraft?.add,
      removedValues: versionDraft?.remove
    });

    if (launch.id === launchId) {
      break;
    }
  }

  return {
    selectedTerritories,
    selectedDubs,
    selectedSubs,
    selectedVersions
  };
};

/**
 * Builds the resolved state used to validate a launch:
 * - added languages validate against resolved territories
 * - added territories validate against resolved languages
 */
export const getLaunchValidationExpansionState = ({
  launch,
  launchList,
  versionId,
  originalSelectedTerritories,
  originalSelectedDubs,
  originalSelectedSubs,
  originalSelectedVersions
}: {
  launch: Launch;
  launchList: Launch[];
  versionId: number;
  originalSelectedTerritories: string[];
  originalSelectedDubs: string[];
  originalSelectedSubs: string[];
  originalSelectedVersions: (TitleEntity | TitleEntityV2)['id'][];
}): LaunchValidationExpansionState => {
  const resolvedAfterLaunch = getResolvedRightsStateAtLaunch({
    launchList,
    versionId,
    launchId: launch.id,
    originalSelectedTerritories,
    originalSelectedDubs,
    originalSelectedSubs,
    originalSelectedVersions
  });

  const territoryDraft = launch.incrementalLaunchRightsDraftByTab?.territories;
  const audioDraft = launch.incrementalLaunchRightsDraftByTab?.audio;
  const timedtextDraft = launch.incrementalLaunchRightsDraftByTab?.timedtext;
  const versionDraft = launch.incrementalLaunchVersions;

  return {
    addedTerritories: territoryDraft?.add ?? [],
    addedDubs: audioDraft?.add ?? [],
    addedSubs: timedtextDraft?.add ?? [],
    addedVersions: versionDraft?.add ?? [],
    resolvedTerritories: resolvedAfterLaunch.selectedTerritories,
    resolvedDubs: resolvedAfterLaunch.selectedDubs,
    resolvedSubs: resolvedAfterLaunch.selectedSubs,
    resolvedVersions: resolvedAfterLaunch.selectedVersions
  };
};
